'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { useCart } from '@/contexts/CartContext';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
}

interface CartSummaryProps {
  className?: string;
  /** On menu page: show cart as side icon that opens/closes a slide-out panel on desktop */
  asDrawer?: boolean;
}

const CartSummary = ({ className = '', asDrawer = false }: CartSummaryProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { cartItems, updateQuantity, removeFromCart } = useCart();
  const items = cartItems;

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cgst = subtotal * 0.025; // 2.5% CGST
  const sgst = subtotal * 0.025; // 2.5% SGST
  const DELIVERY_FEE_THRESHOLD = 200;
  const DELIVERY_FEE = 30;
  const deliveryFee = subtotal > 0 && subtotal < DELIVERY_FEE_THRESHOLD ? DELIVERY_FEE : 0;
  const total = subtotal + cgst + sgst + deliveryFee;

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    updateQuantity(itemId, newQuantity);
  };

  const handleRemoveItem = (itemId: string) => {
    removeFromCart(itemId);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const cartContent = (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold text-foreground">Your Cart</h2>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
            {totalItems}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {items.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <Icon name="ShoppingCartIcon" size={48} className="text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">Your cart is empty</p>
            <p className="mt-2 text-xs text-muted-foreground">
              Add items from the menu to get started
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div
                key={item.id}
                className="rounded-lg border border-border bg-background p-4 transition-smooth hover:shadow-sm"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-foreground">{item.name}</h3>
                    <p className="mt-1 text-data text-sm font-medium text-primary">
                      ₹{item.price.toFixed(2)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(item.id)}
                    className="rounded-md p-1 text-muted-foreground transition-smooth hover:bg-muted hover:text-error focus-ring"
                    aria-label={`Remove ${item.name} from cart`}
                  >
                    <Icon name="TrashIcon" size={18} />
                  </button>
                </div>
                <div className="mt-3 flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-foreground transition-smooth hover:bg-muted focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Decrease quantity"
                  >
                    <Icon name="MinusIcon" size={16} />
                  </button>
                  <span className="text-data w-12 text-center text-sm font-medium text-foreground">
                    {item.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-foreground transition-smooth hover:bg-muted focus-ring"
                    aria-label="Increase quantity"
                  >
                    <Icon name="PlusIcon" size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {items.length > 0 && (
        <div className="border-t border-border bg-muted px-6 py-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Subtotal</span>
              <span className="text-data font-medium">₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>CGST (2.5%)</span>
              <span className="text-data font-medium">₹{cgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>SGST (2.5%)</span>
              <span className="text-data font-medium">₹{sgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Delivery Fee</span>
              <span className="text-data font-medium">₹{deliveryFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t border-border pt-2 text-base font-semibold text-foreground">
              <span>Total</span>
              <span className="text-data">₹{total.toFixed(2)}</span>
            </div>
          </div>
          <Link
            href="/order-checkout"
            className="mt-4 flex w-full items-center justify-center space-x-2 rounded-lg bg-accent px-6 py-3 text-sm font-medium text-accent-foreground shadow-md transition-smooth hover-lift active-press focus-ring hover:shadow-lg"
          >
            <span>Proceed to Checkout</span>
            <Icon name="ArrowRightIcon" size={18} />
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop: full sidebar (default) or drawer icon + slide-out when asDrawer */}
      {!asDrawer && (
      <aside
        className={`hidden lg:block lg:sticky lg:top-20 lg:self-start lg:h-[calc(100vh-5rem)] lg:max-h-[calc(100vh-5rem)] lg:w-96 lg:border-l lg:border-border lg:bg-card lg:shadow-lg ${className}`}
        aria-label="Shopping cart"
      >
        {cartContent}
      </aside>
      )}

      {/* Desktop drawer: cart icon + slide-out panel (menu page only) */}
      {asDrawer && (
        <>
          <button
            type="button"
            onClick={toggleExpanded}
            className="hidden lg:flex fixed top-24 right-4 z-50 h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-smooth hover:bg-primary/90 focus-ring"
            aria-label="Open cart"
            aria-expanded={isExpanded}
          >
            <Icon name="ShoppingCartIcon" size={24} />
            {totalItems > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
                {totalItems}
              </span>
            )}
          </button>
          {isExpanded && (
            <>
              <div
                className="hidden lg:block fixed inset-0 z-40 bg-black/30"
                onClick={() => setIsExpanded(false)}
                aria-hidden="true"
              />
              <div
                className="hidden lg:flex fixed top-0 right-0 z-50 h-full w-full max-w-sm flex-col bg-card shadow-xl border-l border-border"
                role="dialog"
                aria-modal="true"
                aria-label="Your cart"
              >
                <div className="relative flex-1 flex flex-col min-h-0">
                  <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="absolute top-3 right-3 z-10 rounded-lg p-2 text-muted-foreground hover:bg-muted focus-ring"
                    aria-label="Close cart"
                  >
                    <Icon name="XMarkIcon" size={20} />
                  </button>
                <div className="flex-1 overflow-y-auto min-h-0">
                  {cartContent}
                </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Mobile Cart Button */}
      <button
        type="button"
        onClick={toggleExpanded}
        className="fixed bottom-20 right-6 z-200 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl transition-smooth hover-lift active-press focus-ring lg:hidden"
        aria-label="View cart"
        aria-expanded={isExpanded}
      >
        <Icon name="ShoppingBagIcon" size={24} />
        {totalItems > 0 && (
          <span className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
            {totalItems}
          </span>
        )}
      </button>

      {/* Mobile Cart Panel */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-300 bg-background lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Shopping cart"
        >
          <div className="flex h-full flex-col">
            {/* Mobile Cart Header */}
            <div className="flex h-16 items-center justify-between border-b border-border px-4">
              <h2 className="font-heading text-lg font-semibold text-foreground">
                Your Cart ({totalItems})
              </h2>
              <button
                type="button"
                onClick={toggleExpanded}
                className="rounded-lg p-2 text-foreground transition-smooth hover:bg-muted focus-ring"
                aria-label="Close cart"
              >
                <Icon name="XMarkIcon" size={24} />
              </button>
            </div>

            {/* Mobile Cart Items */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <Icon name="ShoppingCartIcon" size={48} className="text-muted-foreground" />
                  <p className="mt-4 text-sm text-muted-foreground">Your cart is empty</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Add items from the menu to get started
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-lg border border-border bg-card p-4 transition-smooth"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-foreground">{item.name}</h3>
                          <p className="mt-1 text-data text-sm font-medium text-primary">
                            ₹{item.price.toFixed(2)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          className="rounded-md p-1 text-muted-foreground transition-smooth hover:bg-muted hover:text-error focus-ring"
                          aria-label={`Remove ${item.name} from cart`}
                        >
                          <Icon name="TrashIcon" size={18} />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-background text-foreground transition-smooth hover:bg-muted focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label="Decrease quantity"
                        >
                          <Icon name="MinusIcon" size={18} />
                        </button>
                        <span className="text-data w-12 text-center text-base font-medium text-foreground">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-background text-foreground transition-smooth hover:bg-muted focus-ring"
                          aria-label="Increase quantity"
                        >
                          <Icon name="PlusIcon" size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Cart Summary */}
            {items.length > 0 && (
              <div className="border-t border-border bg-muted px-4 py-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span className="text-data font-medium">₹{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>CGST (2.5%)</span>
                    <span className="text-data font-medium">₹{cgst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>SGST (2.5%)</span>
                    <span className="text-data font-medium">₹{sgst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Delivery Fee</span>
                    <span className="text-data font-medium">₹{deliveryFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t border-border pt-2 text-base font-semibold text-foreground">
                    <span>Total</span>
                    <span className="text-data">₹{total.toFixed(2)}</span>
                  </div>
                </div>
                <Link
                  href="/order-checkout"
                  onClick={toggleExpanded}
                  className="mt-4 flex w-full items-center justify-center space-x-2 rounded-lg bg-accent px-6 py-3 text-base font-medium text-accent-foreground shadow-md transition-smooth active-press focus-ring"
                >
                  <span>Proceed to Checkout</span>
                  <Icon name="ArrowRightIcon" size={20} />
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CartSummary;
